const isLocal = typeof window !== 'undefined' && 
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

const API_BASE = isLocal 
  ? 'http://localhost:5009/api' 
  : (process.env.REACT_APP_API_BASE || 'https://datapylot.ctronicsinfotech.in/api');


const API_ORIGIN = API_BASE.replace(/\/api\/?$/, '');

function getToken() {
  return localStorage.getItem('token');
}

async function request(path: string, options: RequestInit = {}) {
  const isFormData = options.body instanceof FormData;
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string> || {}),
  };
  if (!isFormData && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }
  const token = getToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const url = `${API_BASE}${path}`;
  const method = (options.method || 'GET').toUpperCase();
  const noCache = (options as any)?.noCache === true;
  const fetchCache: RequestCache | undefined = method === 'GET' && !noCache ? 'default' : 'no-store';

  const res = await fetch(url, { ...options, headers, cache: fetchCache });
  if (res.status === 401) {
    try {
      localStorage.removeItem('token');
      localStorage.removeItem('userData');
    } catch { }
    try {
      if (typeof window !== 'undefined') {
        if (window.location?.pathname?.startsWith('/admin')) {
          window.location.assign('/admin/login');
        } else {
          // Redirect to home with a prompt to log in
          window.location.assign('/?auth=login&expired=true');
        }
      }
    } catch { }
  }
  if (!res.ok) {
    const ct = res.headers.get('content-type') || '';
    try {
      if (ct.includes('application/json')) {
        const data = await res.json();
        if (Array.isArray((data as any)?.errors) && (data as any).errors.length) {
          const msg = (data as any).errors
            .map((e: any) => {
              const field = e?.path || e?.param || e?.field || '';
              const m = e?.msg || e?.message || 'Invalid value';
              return field ? `${field}: ${m}` : String(m);
            })
            .join(' | ');
          throw new Error(msg || `HTTP ${res.status}`);
        }
        throw new Error((data as any)?.message || `HTTP ${res.status}`);
      } else {
        const msg = await res.text();
        throw new Error(msg || `HTTP ${res.status}`);
      }
    } catch (e: any) {
      throw new Error(e?.message || `HTTP ${res.status}`);
    }
  }
  const ct = res.headers.get('content-type') || '';
  return ct.includes('application/json') ? res.json() : res.text();
}

export const api = {
  login: (username: string, password: string) => request('/auth/login', { method: 'POST', body: JSON.stringify({ username, password }) }),
  socialLogin: (provider: string) => new Promise((resolve) => {
    setTimeout(() => {
      resolve({
        token: 'mock-social-token',
        user: {
          id: 999,
          username: `${provider}_user`,
          role: 'student',
          email: `user@${provider}.com`
        }
      });
    }, 1000);
  }),
  forgotPassword: (email: string) => request('/auth/forgot-password', { method: 'POST', body: JSON.stringify({ email }) }),
  verifyOtp: (email: string, otp: string) => request('/auth/verify-otp', { method: 'POST', body: JSON.stringify({ email, otp }) }),
  resetPassword: (data: any) => request('/auth/reset-password', { method: 'POST', body: JSON.stringify(data) }),
  register: (data: any) => request('/auth/register', { method: 'POST', body: JSON.stringify(data) }),
  users: {
    list: (search = '') => request(`/users${search ? `?search=${encodeURIComponent(search)}` : ''}`),
    getMe: () => request('/users/me', ({ noCache: true } as any)),
    create: (data: any) => request('/users', { method: 'POST', body: JSON.stringify(data) }),
    updateMe: (data: any) => request('/users/me', { method: 'PUT', body: JSON.stringify(data) }),
    update: (id: number, data: any) => request(`/users/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id: number) => request(`/users/${id}`, { method: 'DELETE' }),
  },
  courses: {
    list: (search = '', type?: string) => {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (type) params.append('type', type);
      const qs = params.toString();
      return request(`/courses${qs ? `?${qs}&` : '?'}__t=${Date.now()}`, ({ noCache: true } as any));
    },
    create: (data: any) => request('/courses', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: number, data: any) => request(`/courses/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id: number) => request(`/courses/${id}`, { method: 'DELETE' }),
    get: (id: number) => request(`/courses/${id}`),
    enrollments: (id: number) => request(`/enrollments?all=true&course_id=${id}`),
    uploadImage: (file: File) => {
      const form = new FormData();
      form.append('file', file);
      return request('/uploads/course-image', { method: 'POST', body: form });
    },
    uploadPackage: (form: FormData) => request('/courses/upload-package', { method: 'POST', body: form }),
    bulkCsv: (form: FormData) => request('/courses/bulk-csv', { method: 'POST', body: form }),
  },
  categories: {
    list: () => request('/categories', ({ noCache: true } as any)),
    get: (id: number) => request(`/categories/${id}`),
    create: (data: any) => request('/categories', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: number, data: any) => request(`/categories/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id: number) => request(`/categories/${id}`, { method: 'DELETE' }),
  },
  uploads: {
    courseImage: (file: File) => {
      const form = new FormData();
      form.append('file', file);
      return request('/uploads/course-image', { method: 'POST', body: form });
    },
    materialFile: (file: File) => {
      const form = new FormData();
      form.append('file', file);
      return request('/uploads/material-file', { method: 'POST', body: form });
    },
    categoryLogo: (file: File) => {
      const form = new FormData();
      form.append('file', file);
      return request('/uploads/category-logo', { method: 'POST', body: form });
    },
  },
  modules: {
    list: () => request('/modules'),
    create: (data: any) => request('/modules', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: number, data: any) => request(`/modules/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id: number) => request(`/modules/${id}`, { method: 'DELETE' }),
  },
  notes: {
    list: (courseId: number) => request(`/course-content-notes?course_id=${courseId}`, ({ noCache: true } as any)),
    create: (data: any) => request('/course-content-notes', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: number, data: any) => request(`/course-content-notes/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id: number) => request(`/course-content-notes/${id}`, { method: 'DELETE' }),
  },
  materials: {
    list: () => request('/materials'),
    create: (data: any) => request('/materials', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: number, data: any) => request(`/materials/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id: number) => request(`/materials/${id}`, { method: 'DELETE' }),
    forUser: (courseId?: number) => request(`/materials/user${courseId ? `?course_id=${courseId}` : ''}`),
    view: (id: number) => request(`/materials/${id}/view`, { method: 'POST' }),
  },
  enrollments: {
    list: (params?: any) => {
      let url = '/enrollments';
      const requestOptions: any = {};
      if (params) {
        if (params.noCache) {
          requestOptions.noCache = true;
          delete params.noCache;
        }
        const q = new URLSearchParams(params).toString();
        if (q) url += `?${q}`;
      }
      return request(url, requestOptions);
    },
    create: (data: any) => request('/enrollments', { method: 'POST', body: JSON.stringify(data) }),
    updateProgress: (courseId: number, progress: number) => request(`/enrollments/${courseId}`, { method: 'PATCH', body: JSON.stringify({ progress }) }),
  },
  practice: {
    list: (courseId?: number) => request(`/practice${courseId ? `?course_id=${courseId}` : ''}`),
    get: (id: number) => request(`/practice/${id}`),
    create: (data: any) => request('/practice', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: number, data: any) => request(`/practice/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id: number) => request(`/practice/${id}`, { method: 'DELETE' }),
    execute: (id: number, data: { language: string; code: string; stdin?: string }) => request(`/practice/${id}/execute`, { method: 'POST', body: JSON.stringify(data) }),
    freeRun: (data: { language: string; code: string; stdin?: string }) => request('/practice/free-run', { method: 'POST', body: JSON.stringify(data) }),
    getExecutions: (id: number) => request(`/practice/${id}/executions`),
    myExecutions: () => request('/practice/executions/me'),
  },
  tests: {
    list: (courseId?: number) => {
      const params = new URLSearchParams();
      if (courseId) params.append('course_id', String(courseId));
      params.append('__t', String(Date.now()));
      return request(`/tests?${params.toString()}`, ({ noCache: true } as any));
    },
    get: (id: number) => request(`/tests/${id}`),
    getAdmin: (id: number) => request(`/tests/${id}/admin`),
    create: (data: any) => request('/tests', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: number, data: any) => request(`/tests/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id: number) => request(`/tests/${id}`, { method: 'DELETE' }),
    attempt: (id: number, data: any) => request(`/tests/${id}/attempts`, { method: 'POST', body: JSON.stringify(data) }),
    getAttempts: (id: number) => request(`/tests/${id}/attempts/all`),
    myAttempts: (courseId?: number) => request(`/tests/attempts/me${courseId ? `?course_id=${courseId}` : ''}`),
    // Question management
    createQuestion: (testId: number, data: any) => request(`/tests/${testId}/questions`, { method: 'POST', body: JSON.stringify(data) }),
    updateQuestion: (questionId: number, data: any) => request(`/tests/questions/${questionId}`, { method: 'PUT', body: JSON.stringify(data) }),
    deleteQuestion: (questionId: number) => request(`/tests/questions/${questionId}`, { method: 'DELETE' }),
  },
  certificates: {
    list: (filters?: any) => {
      // IMPORTANT: Never send user_id from frontend.
      // The backend filters by req.user.id (JWT) automatically.
      // This prevents any possibility of data leakage between users.
      let url = '/certificates';
      const requestOptions: any = { noCache: true }; // Always bypass cache
      if (filters) {
        const qs: string[] = [];
        // Only allow course_id filter - NEVER user_id (backend enforces user scope)
        if (filters.course_id) qs.push(`course_id=${filters.course_id}`);
        // Add cache-buster timestamp to ensure fresh data
        qs.push(`_t=${Date.now()}`);
        if (qs.length) url += `?${qs.join('&')}`;
      } else {
        url += `?_t=${Date.now()}`;
      }
      return request(url, requestOptions);
    },
    issue: (data: any) => request('/certificates', { method: 'POST', body: JSON.stringify(data) }),
    issueSelf: (course_id: number, isModular = false, isFree = false) => request('/certificates/issue-self', { method: 'POST', body: JSON.stringify({ course_id, isModular, isFree }) }),
    generate: (enrollmentId: number) => request(`/certificates/generate/${enrollmentId}`, { method: 'POST' }),
    download: (certificateId: number) => {
      // For download, we usually redirect or open a new window, but if we need a signed URL or blob:
      // Since the backend returns a file stream, we can't just use request() which expects JSON.
      // However, the downloadUrl returned by list() is likely a direct link or we can construct it.
      // But let's add a method to get the download URL or trigger it.
      // Actually, my UserHome logic uses window.open(cert.downloadUrl).
      // But handleDownload in CertificateCard uses api.certificates.download(id).
      // So I should implement this to trigger download.
      window.open(`${API_ORIGIN}/api/certificates/download/${certificateId}?token=${getToken()}`, '_blank');
      return Promise.resolve();
    },
    delete: (id: number) => request(`/certificates/${id}`, { method: 'DELETE' }),
  },
  finance: {
    list: () => request('/finance'),
    getMine: () => request('/finance/me'),
    create: (data: any) => request('/finance', { method: 'POST', body: JSON.stringify(data) }),
  },

  settings: {
    get: () => request('/settings'),
    save: (data: any) => request('/settings', { method: 'POST', body: JSON.stringify(data) }),
  },
  reports: {
    activities: () => request('/reports/activities'),
  },
  metrics: {
    summary: () => request('/metrics/summary'),
  },
  analytics: {
    adminDashboardStats: () => request('/metrics/summary'),
    adminUsers: () => request('/analytics/users'),
    adminCourses: () => request('/analytics/courses'),
    adminRevenue: () => request('/analytics/revenue'),
    adminEngagement: () => request('/analytics/engagement'),
  },
  modularCourses: {
    listCategories: () => request('/categories'),
    enroll: (id: number) => request('/enrollments', { method: 'POST', body: JSON.stringify({ course_id: id }) }),
    listEnrollments: (params?: any) => request('/enrollments'),
  },
  freeCourses: {
    listCourses: () => request('/courses?type=free'),
    enroll: (id: number) => request('/enrollments', { method: 'POST', body: JSON.stringify({ course_id: id }) }),
    listEnrollments: (params?: any) => request('/enrollments'),
  },
  careerPaths: {
    list: () => request('/career-paths'),
    get: (slug: string) => request(`/career-paths/${slug}`),
    adminAll: () => request('/career-paths/admin/all'),
    adminGetById: (id: number) => request(`/career-paths/admin/${id}`),
    create: (data: any) => request('/career-paths', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: number, data: any) => request(`/career-paths/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id: number) => request(`/career-paths/${id}`, { method: 'DELETE' }),
  },
  get: (path: string, options?: any) => request(path, options),
  post: (path: string, body: any, options?: any) => request(path, { ...options, method: 'POST', body: JSON.stringify(body) }),
  put: (path: string, body: any, options?: any) => request(path, { ...options, method: 'PUT', body: JSON.stringify(body) }),
  delete: (path: string, options?: any) => request(path, { ...options, method: 'DELETE' }),
};

export default api;

export { API_BASE, API_ORIGIN };
